/**
 * Pause overlay (ui.overlay = 'pause' | 'move_list'): RESUME · MOVE LIST · RESTART (offline) · SETTINGS
 * shortcuts (screen shake / reduced motion / CRT toggles) · QUIT TO MENU with an inline confirmation. The move list
 * shows every control with keycaps on the player's own device, the signature special with its counter and the
 * counter square; ◀ ▶ switches between the two heads.
 */
import './pause.css';
import { useEffect, useState } from 'preact/hooks';
import { characterById, specialInfo, SpecialsMode, STAMINA, type CharacterId } from '@hf/shared';
import { useActions, useServices } from '../actions';
import { Head, Hints, Key, SpecialIcon } from '../components';
import { ButtonCap, promptDevice } from '../hud/common';
import { LAYER, useFocusGrid, useMenuLayer } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { ui } from '@hf/client-core/ui-model/store';
import { COUNTER_SQUARE, moveSectionsFor, type MoveKey } from '@hf/client-core/ui-model/movelist';
import { openHowToPlay, takePauseFocus } from '../tutorial/HowToPlay';

/**
 * Resume on the next macrotask: the match scene's own menu handler runs after ours for the same key and would
 * re-open the pause menu if it saw `overlay === 'none'` already.
 */
const deferred = (fn: () => void): void => void setTimeout(fn, 0);

export function PauseOverlay() {
  const overlay = ui.overlay.value;
  const { input } = useServices();
  const open = overlay === 'pause' || overlay === 'move_list';
  useEffect(() => {
    if (!open) return;
    input.setTouchSuspended(true);
    return () => input.setTouchSuspended(false);
  }, [open]);
  if (overlay === 'move_list') return <MoveList />;
  if (overlay === 'pause') return <PauseMenu />;
  return null;
}

function PauseMenu() {
  const actions = useActions();
  const { audio } = useServices();
  const [confirm, setConfirm] = useState(false);
  const setup = ui.matchSetup.value;
  const online = !!setup?.online;
  const tutorial = setup?.mode === 'tutorial';
  const tutPhase = ui.tutorial.value?.phase;
  // Game Day has no steps to retry (it is a game); slate / beat are steps
  const playing = tutorial && (tutPhase === 'beat' || tutPhase === 'slate');
  const gameDay = playing && ui.tutorial.value?.lessonId === 'scrimmage';
  const tutorialStep = playing && !gameDay;
  const s = ui.settings.value;
  const resume = (): void => {
    playSfx(audio, UI_SFX.back);
    deferred(() => actions.resume());
  };

  const items: { id: string; label: string; value?: string; run: () => void }[] = [
    { id: 'resume', label: 'RESUME', run: resume },
    {
      id: 'moves',
      label: 'MOVE LIST',
      run: () => {
        playSfx(audio, UI_SFX.confirm);
        ui.overlay.value = 'move_list';
      },
    },
    {
      id: 'howto',
      label: 'HOW TO PLAY',
      run: () => {
        playSfx(audio, UI_SFX.confirm);
        openHowToPlay();
      },
    },
    ...(online
      ? []
      : tutorial
        ? [
            // the step items only while a lesson step is being played (on a clear / final card they did nothing)
            ...(tutorialStep
              ? [
                  {
                    id: 'retry',
                    label: 'RETRY THIS STEP',
                    run: () => deferred(() => (actions.resume(), ui.tutorialCommand({ kind: 'retry' }))),
                  },
                  {
                    id: 'skip',
                    label: 'SKIP THIS STEP',
                    run: () => deferred(() => (actions.resume(), actions.tutorialSkip())),
                  },
                ]
              : gameDay
                ? [
                    {
                      id: 'skip',
                      label: 'END GAME',
                      run: () => deferred(() => (actions.resume(), actions.tutorialSkip())),
                    },
                  ]
                : []),
            { id: 'lessons', label: 'ALL LESSONS', run: () => deferred(() => actions.goto('tutorial')) },
          ]
        : [{ id: 'restart', label: 'RESTART MATCH', run: () => deferred(() => actions.rematch()) }]),
    {
      id: 'shake',
      label: 'SCREEN SHAKE',
      value: s.screenShake > 0 ? 'ON' : 'OFF',
      run: () => ui.updateSettings({ screenShake: s.screenShake > 0 ? 0 : 1 }),
    },
    {
      id: 'crt',
      label: 'CRT FILTER',
      value: s.crt ? 'ON' : 'OFF',
      run: () => ui.updateSettings({ crt: !s.crt }),
    },
    {
      id: 'quit',
      label: online ? 'FORFEIT & LEAVE' : 'QUIT TO MENU',
      run: () => {
        playSfx(audio, UI_SFX.error);
        setConfirm(true);
      },
    },
  ];

  // back from HOW TO PLAY: the focus returns to its row (it used to land on RESUME)
  const [initial] = useState(() => {
    const row = takePauseFocus();
    return Math.max(
      0,
      items.findIndex((it) => it.id === row),
    );
  });
  const grid = useFocusGrid({
    count: items.length,
    initial,
    priority: LAYER.OVERLAY,
    active: !confirm,
    onMove: () => playSfx(audio, UI_SFX.move),
    onConfirm: (i) => items[i]?.run(),
    onAdjust: (i) => {
      const it = items[i];
      if (it?.value !== undefined) it.run();
    },
    onBack: resume,
    onPause: resume,
  });

  const confirmGrid = useFocusGrid({
    count: 2,
    horizontal: true,
    initial: 1,
    priority: LAYER.MODAL,
    active: confirm,
    onConfirm: (i) => {
      if (i === 0) deferred(() => actions.quitMatch());
      else setConfirm(false);
    },
    onBack: () => setConfirm(false),
  });

  const quitCopy = quitConfirmCopy(
    online ? (setup?.online?.kind === 'ranked' ? 'ranked' : 'online') : (setup?.mode ?? 'quick'),
  );
  return (
    <div
      class={`hf-pause interactive ${online ? 'online' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={online ? 'Match menu' : 'Paused'}
    >
      {/* Online nothing pauses: a light scrim and a side panel keep the live field, the HUD and the play clock
          readable behind the menu (QA r3: a full blur titled PAUSED while the opponent kept scoring). */}
      <div class="hf-pause__scrim" onClick={resume} />
      <div class={`hf-pause__panel ${confirm ? 'under' : ''}`} aria-hidden={confirm ? 'true' : undefined}>
        <div class="hf-pause__title t-display">{online ? 'MATCH MENU' : 'PAUSED'}</div>
        {online ? (
          <div class="hf-pause__note t-broadcast">
            <span class="hf-pause__live" aria-hidden="true" />
            LIVE · THE MATCH KEEPS RUNNING
          </div>
        ) : null}
        <div class="hf-pause__items">
          {items.map((it, i) => (
            <button key={it.id} class={`hf-pause__item ${it.id}`} {...grid.item(i)}>
              <span class="t-display">{it.label}</span>
              {it.value !== undefined ? (
                <span class={`hf-pause__val t-broadcast ${it.value === 'ON' ? 'on' : ''}`}>{it.value}</span>
              ) : null}
            </button>
          ))}
        </div>
        <Hints
          items={[
            ['up|down', 'SELECT'],
            ['confirm', 'OK'],
            ['back', 'RESUME'],
          ]}
        />
      </div>
      {confirm ? (
        <div class="hf-confirm" role="alertdialog" aria-label={quitCopy.title}>
          <div class="hf-confirm__title t-display">{quitCopy.title}</div>
          <p>{quitCopy.body}</p>
          <div class="hf-confirm__btns">
            <button class="hf-btn red" {...confirmGrid.item(0)}>
              {quitCopy.verb}
            </button>
            <button class="hf-btn dark" {...confirmGrid.item(1)}>
              KEEP PLAYING
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The quit confirmation's words. A Football 101 lesson is not a "match": leaving it never loses finished lessons
 * (newcomers read "progress will be lost" as their camp progress). Online only RANKED games are rated (the server
 * moves Elo on ranked results only), so a quick match or private room must not threaten a ranked loss (QA r4):
 * there the opponent simply gets the win.
 */
export const quitConfirmCopy = (kind: string): { title: string; body: string; verb: string } =>
  kind === 'ranked'
    ? { title: 'FORFEIT THIS MATCH?', body: 'You take the loss and your rating drops.', verb: 'FORFEIT' }
    : kind === 'online'
      ? {
          title: 'FORFEIT THIS MATCH?',
          body: 'Your opponent gets the win. No rating change.',
          verb: 'FORFEIT',
        }
      : kind === 'tutorial'
        ? { title: 'LEAVE THIS LESSON?', body: 'Finished lessons are saved.', verb: 'LEAVE' }
        : kind === 'practice'
          ? { title: 'LEAVE PRACTICE?', body: 'Practice has no score to lose.', verb: 'LEAVE' }
          : { title: 'QUIT THIS MATCH?', body: 'The score of this match will be lost.', verb: 'QUIT' };

function MoveList() {
  const actions = useActions();
  const setup = ui.matchSetup.value;
  const { input, audio } = useServices();
  const humans = ([0, 1] as const).filter((i) => setup?.slots[i].kind === 'human');
  const sides: (0 | 1)[] = setup ? [0, 1] : [0];
  const [tab, setTab] = useState<0 | 1>(humans[0] ?? 0);
  const [scroll, setScroll] = useState<HTMLElement | null>(null);
  const character: CharacterId = setup?.config.characters[tab] ?? 'rookie';
  const def = characterById(character);
  const sp = specialInfo(def.signature);
  const specialsOn = (setup?.config.specials ?? SpecialsMode.SIGNATURE) !== SpecialsMode.OFF;
  const device = promptDevice(setup, tab, input.lastActiveDevice()) ?? 'kb:solo';
  const back = (): void => {
    playSfx(audio, UI_SFX.back);
    ui.overlay.value = 'pause';
  };
  useMenuLayer(
    (a) => {
      if (a === 'back' || a === 'confirm') back();
      else if (a === 'pause') deferred(() => actions.resume());
      else if (a === 'left' || a === 'right') {
        if (sides.length > 1) {
          setTab((t) => (t === 0 ? 1 : 0));
          playSfx(audio, UI_SFX.move);
        }
      } else if (scroll)
        scroll.scrollBy({ top: (a === 'down' ? 1 : -1) * scroll.clientHeight * 0.4, behavior: 'smooth' });
    },
    true,
    LAYER.OVERLAY,
  );
  const cap = (k: MoveKey, i: number) =>
    k === '+' || k === '→' ? (
      <span class="hf-moves__op" key={i}>
        {k}
      </span>
    ) : k === '×2' || k === 'HOLD' || k === 'AIR' || k === 'DASH' || k === 'LET GO' ? (
      <span class="hf-moves__word t-broadcast" key={i}>
        {k}
      </span>
    ) : (
      <ButtonCap key={i} device={device} button={k} />
    );

  return (
    <div class="hf-pause interactive" role="dialog" aria-modal="true" aria-label="Move list">
      <div class="hf-pause__scrim" onClick={back} />
      <div class="hf-moves">
        <header class="hf-moves__head">
          <div class="hf-moves__title t-display">MOVE LIST</div>
          <div class="hf-moves__tabs" role="tablist">
            {sides.map((i) => {
              const c = setup?.config.characters[i] ?? 'rookie';
              return (
                <button
                  key={i}
                  role="tab"
                  aria-selected={tab === i}
                  class={`hf-moves__tab ${i === 0 ? 'home' : 'away'} ${tab === i ? 'on' : ''}`}
                  onClick={() => setTab(i)}
                >
                  <Head character={c} class="hf-moves__tabhead" flip={i === 1} />
                  <span class="t-broadcast">{characterById(c).name}</span>
                </button>
              );
            })}
          </div>
        </header>
        <div class="hf-moves__body" ref={setScroll}>
          {specialsOn ? (
            <section class="hf-moves__special">
              <span class="hf-moves__spicon">
                <SpecialIcon special={def.signature} />
              </span>
              <div>
                <div class="t-display hf-moves__spname">
                  {sp.name} <ButtonCap device={device} button="S" />
                </div>
                <p>{sp.blurb}</p>
                <p class="counter">
                  <b>COUNTER:</b> {sp.counter}
                </p>
              </div>
            </section>
          ) : null}
          <div class="hf-moves__grid">
            {moveSectionsFor(ui.matchSetup.value?.config.format).map((sec) => (
              <section class={`hf-moves__sec ${sec.tone}`} key={sec.title}>
                <h3 class="t-broadcast">{sec.title}</h3>
                {sec.moves.map((m) => (
                  <div class="hf-moves__row" key={m.name}>
                    <div class="hf-moves__name">
                      <b class="t-display">{m.name}</b>
                      {m.fake ? <span class="hf-moves__fake t-broadcast">FAKE</span> : null}
                    </div>
                    <div class="hf-moves__keys">{m.keys.map(cap)}</div>
                    <div class="hf-moves__note">{m.note}</div>
                  </div>
                ))}
              </section>
            ))}
            <section class="hf-moves__sec square">
              <h3 class="t-broadcast">THE COUNTER SQUARE</h3>
              <table class="hf-square">
                <thead>
                  <tr>
                    <th>
                      <small>CARRIER ↓ · DEFENDER →</small>
                    </th>
                    {COUNTER_SQUARE.cols.map((c) => (
                      <th key={c} class="t-broadcast">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COUNTER_SQUARE.rows.map((r) => (
                    <tr key={r.name}>
                      <th class="t-broadcast">{r.name}</th>
                      {r.cells.map((w, i) => (
                        <td key={i} class={w}>
                          {w === 'carrier' ? '✓ CARRIER' : '✗ TACKLED'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p class="hf-moves__tip">
                Most fakes cost stamina (the hard count, hesitation and fast-fall are free). Under{' '}
                {STAMINA.minFeintCost} stamina you are <Key label="MUST BE REAL" />: no fakes — and your
                opponent can see it.
              </p>
            </section>
          </div>
        </div>
        <Hints
          items={[
            ...(sides.length > 1 ? ([['left|right', 'HEAD']] as [string, string][]) : []),
            ['up|down', 'SCROLL'],
            ['back', 'BACK'],
          ]}
        />
      </div>
    </div>
  );
}
